import {
  FiLink,
  FiPieChart,
  FiShield,
  FiTrendingUp,
  FiGlobe,
  FiClock,
  FiSmartphone,
  FiImage,
  FiTarget,
  FiGrid,
  FiBarChart2,
} from "react-icons/fi";
import { featurePages as baseFeaturePages, featureHighlights as baseFeatureHighlights } from "./featurePages";
import { BioPagesFeatureMark, CampaignsFeatureMark } from "./featureMedia";

const additionalFeaturePages = {
  "password-protection": {
    slug: "password-protection",
    eyebrow: "Feature · Access Control",
    title: "Password-protected links for gated sharing",
    description:
      "Protect a link with a shared password so only the right audience can reach a destination during private campaigns, previews, or controlled rollouts.",
    icon: FiShield,
    heroStats: [
      { label: "Access control", value: "Simple" },
      { label: "Audience gating", value: "Tight" },
      { label: "Setup time", value: "Instant" },
    ],
    capabilities: [
      {
        title: "Shared-secret access",
        detail:
          "Require the correct password before the visitor reaches the destination page.",
      },
      {
        title: "Controlled distribution",
        detail:
          "Use a single link for private launches, internal handoffs, or gated offers.",
      },
      {
        title: "Fast revocation",
        detail:
          "Rotate the password when a campaign changes or access needs to be reset.",
      },
    ],
    outcomes: [
      "Safer link sharing",
      "Better control over private campaigns",
      "Reduced accidental exposure",
    ],
    useCases: [
      {
        heading: "Internal reviews",
        text: "Share staging or review URLs without exposing them publicly.",
      },
      {
        heading: "VIP offers",
        text: "Gate premium promotions for partners, customers, or event attendees.",
      },
      {
        heading: "Controlled launches",
        text: "Keep sensitive assets behind a password until release day.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a password protection modal screenshot here",
    },
  },
  "mobile-targeting": {
    slug: "mobile-targeting",
    eyebrow: "Feature · Device Targeting",
    title: "Mobile-targeted redirects for device-specific journeys",
    description:
      "Send mobile visitors to a dedicated destination so phone users get an experience built for their screen size and intent.",
    icon: FiSmartphone,
    heroStats: [
      { label: "Device routing", value: "Targeted" },
      { label: "Mobile fit", value: "High" },
      { label: "Setup time", value: "Quick" },
    ],
    capabilities: [
      {
        title: "Mobile URL routing",
        detail:
          "Use a separate destination URL for users detected on mobile devices.",
      },
      {
        title: "Device-aware journeys",
        detail:
          "Keep desktop and mobile experiences aligned with the right landing page.",
      },
      {
        title: "Conversion-focused UX",
        detail:
          "Send smaller screens to faster, clearer destinations that fit the device context.",
      },
    ],
    outcomes: [
      "Better mobile conversion paths",
      "Cleaner device-specific experiences",
      "Less friction for phone users",
    ],
    useCases: [
      {
        heading: "App installs",
        text: "Route phone users to app store or mobile-first install pages.",
      },
      {
        heading: "Mobile campaigns",
        text: "Match ad clicks to pages designed specifically for smaller screens.",
      },
      {
        heading: "Responsive launches",
        text: "Keep desktop and mobile journeys optimized without creating separate links.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a mobile targeting route preview screenshot here",
    },
  },
  "custom-social-previews": {
    slug: "custom-social-previews",
    eyebrow: "Feature · Social Sharing",
    title: "Custom social previews that make every share look polished",
    description:
      "Define the Open Graph title, description, and image so your links preview beautifully in social apps and messaging platforms.",
    icon: FiImage,
    heroStats: [
      { label: "OG control", value: "Full" },
      { label: "Share quality", value: "High" },
      { label: "Brand safety", value: "Strong" },
    ],
    capabilities: [
      {
        title: "Custom metadata",
        detail:
          "Set the preview title and description shown by social crawlers and chat apps.",
      },
      {
        title: "Custom preview art",
        detail:
          "Use a tailored image to match your campaign or brand identity.",
      },
      {
        title: "Crawler-aware delivery",
        detail:
          "Serve metadata that improves how links appear when shared publicly.",
      },
    ],
    outcomes: [
      "Higher-quality social shares",
      "More polished brand presentation",
      "Better click-through from previews",
    ],
    useCases: [
      {
        heading: "Product launches",
        text: "Make every shared link feel like a controlled campaign asset.",
      },
      {
        heading: "Press and PR",
        text: "Ensure media teams and partners see the right message and artwork.",
      },
      {
        heading: "Creator collabs",
        text: "Keep shared short links visually aligned with the creator campaign.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a custom social preview screenshot here",
    },
  },
  "detailed-click-analytics": {
    slug: "detailed-click-analytics",
    eyebrow: "Feature · Analytics",
    title: "Detailed click analytics that explain what is really working",
    description:
      "Understand clicks by geography, browser, device, referrer, and time so every campaign decision is backed by real traffic data.",
    icon: FiPieChart,
    heroStats: [
      { label: "Signals", value: "Rich" },
      { label: "Response time", value: "Live" },
      { label: "Insight depth", value: "High" },
    ],
    capabilities: [
      {
        title: "Traffic breakdowns",
        detail:
          "Analyze clicks across country, city, browser, OS, device, and referrer.",
      },
      {
        title: "Trend tracking",
        detail:
          "Spot performance shifts over time and react to campaign patterns early.",
      },
      {
        title: "Operational reporting",
        detail:
          "Share concise data with stakeholders without exporting to a separate analytics stack.",
      },
    ],
    outcomes: [
      "Better channel decisions",
      "Clearer campaign reporting",
      "Faster optimization cycles",
    ],
    useCases: [
      {
        heading: "Marketing reporting",
        text: "Turn click activity into a clean story for the team and stakeholders.",
      },
      {
        heading: "Audience analysis",
        text: "See which countries, devices, and referrers are driving performance.",
      },
      {
        heading: "Campaign optimization",
        text: "Use click signals to refine targeting, messaging, and distribution.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a click analytics dashboard screenshot here",
    },
  },
  "a-b-testing": {
    slug: "a-b-testing",
    eyebrow: "Feature · Experimentation",
    title: "A/B testing that routes traffic by weight",
    description:
      "Send clicks to multiple destinations with weighted splits so you can test variants and learn which version performs best.",
    icon: FiBarChart2,
    heroStats: [
      { label: "Weighted splits", value: "Yes" },
      { label: "Variants", value: "Multiple" },
      { label: "Decision speed", value: "Fast" },
    ],
    capabilities: [
      {
        title: "Weighted destinations",
        detail:
          "Set how much traffic each variant receives and adjust the mix as results come in.",
      },
      {
        title: "Experiment-ready flows",
        detail:
          "Run distribution tests on landing pages, offers, or creative variants.",
      },
      {
        title: "Outcome analysis",
        detail:
          "Compare click and conversion behavior to identify a winning route.",
      },
    ],
    outcomes: [
      "Clearer test results",
      "Better landing-page decisions",
      "Less guesswork in campaigns",
    ],
    useCases: [
      {
        heading: "Landing page tests",
        text: "Compare two pages and learn which one drives more engagement.",
      },
      {
        heading: "Offer testing",
        text: "Send visitors to multiple promotion pages with controlled weights.",
      },
      {
        heading: "Creative experiments",
        text: "Validate which message or destination performs better at scale.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add an A/B testing configuration screenshot here",
    },
  },
  "geo-targeting": {
    slug: "geo-targeting",
    eyebrow: "Feature · Localization",
    title: "Geo-targeting that routes visitors by country",
    description:
      "Use country-based rules so one short link can send visitors to localized pages, pricing, or offers based on where they are.",
    icon: FiGlobe,
    heroStats: [
      { label: "Country routing", value: "Yes" },
      { label: "Regional fit", value: "High" },
      { label: "Localization", value: "Built-in" },
    ],
    capabilities: [
      {
        title: "Country-specific destinations",
        detail:
          "Define regional destinations that match a visitor's country.",
      },
      {
        title: "Localized campaigns",
        detail:
          "Run one link that automatically adapts to international audiences.",
      },
      {
        title: "Market segmentation",
        detail:
          "Keep country-level traffic flowing to the right campaign page.",
      },
    ],
    outcomes: [
      "More relevant regional experiences",
      "Better international campaign control",
      "Cleaner localized routing",
    ],
    useCases: [
      {
        heading: "Regional product launches",
        text: "Direct each country to the right launch page or offer.",
      },
      {
        heading: "Localized pricing",
        text: "Keep pricing pages aligned with market expectations.",
      },
      {
        heading: "Country-based promotions",
        text: "Deliver region-specific messages without cloning links manually.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a geo-targeting rules screenshot here",
    },
  },
  "retargeting-pixels": {
    slug: "retargeting-pixels",
    eyebrow: "Feature · Attribution",
    title: "Retargeting pixels that keep your audience loops alive",
    description:
      "Add Facebook, Google, and X pixel IDs so your redirect page can support remarketing and audience building.",
    icon: FiTarget,
    heroStats: [
      { label: "Pixel support", value: "Multi-platform" },
      { label: "Audience capture", value: "Yes" },
      { label: "Remarketing", value: "Enabled" },
    ],
    capabilities: [
      {
        title: "Multi-network IDs",
        detail:
          "Store the platform pixel IDs needed for remarketing campaigns.",
      },
      {
        title: "Redirect-page firing",
        detail:
          "Keep tracking scripts active long enough for pixels to execute before redirecting.",
      },
      {
        title: "Audience-building support",
        detail:
          "Use short links as the entry point to remarketing and conversion loops.",
      },
    ],
    outcomes: [
      "Stronger retargeting coverage",
      "Better audience capture",
      "More complete tracking flows",
    ],
    useCases: [
      {
        heading: "Paid acquisition",
        text: "Feed ad platforms with click activity that can be retargeted later.",
      },
      {
        heading: "Remarketing funnels",
        text: "Keep a user in your audience pool after the redirect begins.",
      },
      {
        heading: "Cross-channel campaigns",
        text: "Use one link to support several attribution and remarketing systems.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a retargeting pixel setup screenshot here",
    },
  },
  "conversion-tracking": {
    slug: "conversion-tracking",
    eyebrow: "Feature · Measurement",
    title: "Conversion tracking that connects clicks to outcomes",
    description:
      "Attribute downstream signups, purchases, and custom events back to the link that started the journey.",
    icon: FiTrendingUp,
    heroStats: [
      { label: "Attribution", value: "Signed" },
      { label: "Events", value: "Tracked" },
      { label: "Reporting", value: "Clear" },
    ],
    capabilities: [
      {
        title: "Signed tracking token",
        detail:
          "Use a secure token to connect a conversion event back to the originating click.",
      },
      {
        title: "Goal-based events",
        detail:
          "Track signups, purchases, leads, or custom conversion goals.",
      },
      {
        title: "Destination-level attribution",
        detail:
          "Preserve variant context for A/B tests and downstream reporting.",
      },
    ],
    outcomes: [
      "Better ROI visibility",
      "Clearer funnel attribution",
      "More actionable conversion data",
    ],
    useCases: [
      {
        heading: "Signup funnels",
        text: "Measure whether the original click ended in a successful signup.",
      },
      {
        heading: "Ecommerce purchases",
        text: "Attribute revenue back to a short link and its campaign context.",
      },
      {
        heading: "Lead generation",
        text: "Track form fills or qualified leads from public link traffic.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a conversion tracking snippet screenshot here",
    },
  },
  "link-expiration-fallback": {
    slug: "link-expiration-fallback",
    eyebrow: "Feature · Expiration Handling",
    title: "Link expiration fallback that keeps expired links useful",
    description:
      "Choose whether an expired link redirects somewhere else or shows a branded fallback page with your message and call to action.",
    icon: FiClock,
    heroStats: [
      { label: "Fallback mode", value: "Flexible" },
      { label: "Expired UX", value: "Branded" },
      { label: "Control", value: "Strong" },
    ],
    capabilities: [
      {
        title: "Redirect fallback",
        detail:
          "Send expired traffic to another URL when the campaign is over.",
      },
      {
        title: "Branded expired page",
        detail:
          "Show a controlled message and CTA instead of a dead end.",
      },
      {
        title: "Auto-redirect timers",
        detail:
          "Optionally count down to a fallback redirect after the expired page appears.",
      },
    ],
    outcomes: [
      "Cleaner expired-link handling",
      "Better user experience after expiry",
      "Fewer dead-end campaign visits",
    ],
    useCases: [
      {
        heading: "Seasonal campaigns",
        text: "Keep expired holiday or flash-sale links useful after the window ends.",
      },
      {
        heading: "Event pages",
        text: "Guide visitors to the next relevant action after a live event closes.",
      },
      {
        heading: "Time-boxed offers",
        text: "Swap the expired destination for a new campaign or branded page.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a link expiration fallback screenshot here",
    },
  },
  "bio-pages": {
    slug: "bio-pages",
    eyebrow: "Feature · Link in Bio",
    title: "Bio pages that turn one profile link into a conversion hub",
    description:
      "Build a customizable bio page with multiple links, social profiles, and branded styling for a cleaner creator or personal-brand presence.",
    icon: BioPagesFeatureMark,
    heroStats: [
      { label: "Link hub", value: "Multi-link" },
      { label: "Social profiles", value: "Built-in" },
      { label: "Customization", value: "Flexible" },
    ],
    capabilities: [
      {
        title: "Multi-link landing pages",
        detail:
          "Collect multiple calls to action in one public destination.",
      },
      {
        title: "Social profile support",
        detail:
          "Connect creator and brand profiles across the platforms your audience uses.",
      },
      {
        title: "Theme and style control",
        detail:
          "Adjust visuals so the bio page stays aligned with your brand identity.",
      },
    ],
    outcomes: [
      "More useful profile traffic",
      "A better creator storefront",
      "Centralized social presence",
    ],
    useCases: [
      {
        heading: "Creators",
        text: "Turn one profile link into a structured hub for content and offers.",
      },
      {
        heading: "Personal brands",
        text: "Present portfolio, socials, and key links in one clean page.",
      },
      {
        heading: "Campaign microsites",
        text: "Use a bio page when you need a lightweight public hub.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a bio page preview screenshot here",
    },
  },
  campaigns: {
    slug: "campaigns",
    eyebrow: "Feature · Campaign Management",
    title: "Campaigns that group related links into one performance view",
    description:
      "Organize related links into campaigns so marketers can manage launches, seasonal pushes, and product programs from a single place.",
    icon: CampaignsFeatureMark,
    heroStats: [
      { label: "Grouping", value: "Structured" },
      { label: "Analytics", value: "Aggregated" },
      { label: "Workflow", value: "Organized" },
    ],
    capabilities: [
      {
        title: "Related link grouping",
        detail:
          "Bundle links together for one launch, initiative, or business unit.",
      },
      {
        title: "Aggregated analytics",
        detail:
          "See combined performance across everything that belongs to a campaign.",
      },
      {
        title: "Operational control",
        detail:
          "Create a cleaner workspace for large marketing calendars and promotions.",
      },
    ],
    outcomes: [
      "Cleaner campaign reporting",
      "Less manual link juggling",
      "Easier launch coordination",
    ],
    useCases: [
      {
        heading: "Product launches",
        text: "Group every link for the launch under one campaign umbrella.",
      },
      {
        heading: "Seasonal promotions",
        text: "Keep black-friday or holiday links together for easier tracking.",
      },
      {
        heading: "Agency workflows",
        text: "Manage multiple client programs without mixing link performance.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a campaign overview screenshot here",
    },
  },
  "custom-domains": {
    slug: "custom-domains",
    eyebrow: "Feature · Brand Infrastructure",
    title: "Custom domains that make every public link feel branded",
    description:
      "Use your own domains for short links and public pages so every visit reinforces your brand and ownership.",
    icon: FiLink,
    heroStats: [
      { label: "Brand control", value: "High" },
      { label: "Verification", value: "Supported" },
      { label: "Public trust", value: "Stronger" },
    ],
    capabilities: [
      {
        title: "Domain verification",
        detail:
          "Add and verify branded domains from the platform workflow.",
      },
      {
        title: "Shared ownership",
        detail:
          "Keep the domain tied to the user or team that manages the campaign.",
      },
      {
        title: "Brand-consistent surfaces",
        detail:
          "Use the same domain strategy across short links and bio pages.",
      },
    ],
    outcomes: [
      "More trust in shared links",
      "Cleaner branded distribution",
      "Better domain ownership control",
    ],
    useCases: [
      {
        heading: "Enterprise branding",
        text: "Keep all public links under your company domain.",
      },
      {
        heading: "Agency clients",
        text: "Run client campaigns on branded domains that are easy to recognize.",
      },
      {
        heading: "Public microsites",
        text: "Use branded domains for short links and bio pages together.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a custom domain setup screenshot here",
    },
  },
  "bulk-operations": {
    slug: "bulk-operations",
    eyebrow: "Feature · Admin Efficiency",
    title: "Bulk operations that keep large link libraries manageable",
    description:
      "Activate, deactivate, or delete multiple links at once so large accounts can move quickly without repetitive manual work.",
    icon: FiGrid,
    heroStats: [
      { label: "Batch actions", value: "Yes" },
      { label: "Admin time", value: "Lower" },
      { label: "Scale", value: "Ready" },
    ],
    capabilities: [
      {
        title: "Multi-select workflows",
        detail:
          "Select multiple links and apply the same action in one pass.",
      },
      {
        title: "Batch lifecycle control",
        detail:
          "Turn groups of links on or off without editing them individually.",
      },
      {
        title: "Large-account cleanup",
        detail:
          "Remove outdated links quickly when campaigns age out or reorganize.",
      },
    ],
    outcomes: [
      "Less admin overhead",
      "Faster large-scale updates",
      "Cleaner link hygiene",
    ],
    useCases: [
      {
        heading: "Migrations",
        text: "Retire or update old link sets in a controlled batch.",
      },
      {
        heading: "Seasonal updates",
        text: "Enable and disable many campaigns at once around launch windows.",
      },
      {
        heading: "Operations teams",
        text: "Manage large libraries without repetitive single-link edits.",
      },
    ],
    screenshot: {
      width: 960,
      height: 720,
      label: "Add a bulk operations table screenshot here",
    },
  },
};

export const featurePages = {
  ...baseFeaturePages,
  ...additionalFeaturePages,
};

export const featureCatalogSections = [
  {
    eyebrow: "Core platform",
    title: "Core features that power every link",
    subtitle:
      "The essentials that make LinkSquire fast, branded, secure, and ready for day-to-day campaign work.",
    items: [
      featurePages["brand-ready-short-links"],
      featurePages["password-protection"],
      featurePages["mobile-targeting"],
      featurePages["custom-social-previews"],
      featurePages["qr-and-channel-ops"],
      featurePages["fast-global-redirects"],
      featurePages["edit-links-anytime"],
      featurePages.campaigns,
    ],
  },
  {
    eyebrow: "Insight and optimization",
    title: "Measurement and experimentation tools",
    subtitle:
      "Use one platform to understand traffic, test ideas, and route visitors with precision.",
    items: [
      featurePages["detailed-click-analytics"],
      featurePages["live-performance-analytics"],
      featurePages["a-b-testing"],
      featurePages["geo-targeting"],
      featurePages["retargeting-pixels"],
      featurePages["conversion-tracking"],
    ],
  },
  {
    eyebrow: "Governance and growth",
    title: "Operational controls for scaling cleanly",
    subtitle:
      "Keep campaigns organized with expiration handling, branded presence, and admin efficiency features.",
    items: [
      featurePages["link-expiration-fallback"],
      featurePages["expiration-and-controls"],
      featurePages["bio-pages"],
      featurePages["custom-domains"],
      featurePages["bulk-operations"],
    ],
  },
];

export const featureHighlights = baseFeatureHighlights;
